import { LinearGradient } from "expo-linear-gradient";
import React, { useRef, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Animated,
  Platform,
  Image,
  ImageBackground,
  Linking,
  ScrollView,
  useWindowDimensions
} from "react-native";
import { AccessibilityInfo } from "react-native";
import { SafeAreaView } from 'react-native-safe-area-context';

import AnimatedButton from "../../components/LandingScreen/AnimatedButton";
import LandingLanguageSelector from "../../components/LandingScreen/LandingLanguageSelector";
import Sparkles from "../../components/LandingScreen/Sparkles";
import Stars from "../../components/LandingScreen/Stars";
import styles from "../../components/LandingScreen/styles";
import VerseCard from "../../components/LandingScreen/VerseCard";
import { spacing } from '../../design-system/tokens';
import { useLanguageStore } from '../../features/quiz-core/languageStore';
import { t } from '../../i18n/translations';

interface LandingScreenProps {
  onGetStarted: () => void;
  onPlayAsGuest?: () => void; // Optional guest mode handler (mobile only)
}

export default function LandingScreen({ onGetStarted, onPlayAsGuest }: LandingScreenProps) {
  // Get current language for translations
  const { language } = useLanguageStore();
  const { width } = useWindowDimensions();
  const isWideLayout = width >= 1080;
  const isPhoneLayout = width < 768;
  const featureChips = [
    t('landing.featureVerseReveal', language),
    t('landing.featureStreaks', language),
    t('landing.featureFamily', language),
  ];

  const [isCardHovered, setCardHovered] = React.useState(false);
  const [isVerseExpanded, setIsVerseExpanded] = React.useState(false);
  const [showConfetti, setShowConfetti] = React.useState(false);
  const [reduceMotion, setReduceMotion] = React.useState(false);
  const buttonScaleAnim = useRef(new Animated.Value(1)).current;
  const buttonShimmerAnim = useRef(new Animated.Value(0.7)).current;

  useEffect(() => {
    if (Platform.OS === 'web') {
      AccessibilityInfo.isReduceMotionEnabled().then(setReduceMotion);
    }
  }, []);

  // Animate button shimmer loop
  useEffect(() => {
    if (!reduceMotion) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(buttonShimmerAnim, {
            toValue: 1,
            duration: 1100,
            useNativeDriver: false
          }),
          Animated.timing(buttonShimmerAnim, {
            toValue: 0.7,
            duration: 1100,
            useNativeDriver: false
          })
        ])
      ).start();
    }
  }, [reduceMotion]);

  // Handle Start Playing press (confetti burst) - Goes to guest mode on mobile, auth on web
  const handleStartPlaying = () => {
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 1200);

    // Mobile: Start in guest mode, web: Go to authentication
    if (onPlayAsGuest && (Platform.OS === 'ios' || Platform.OS === 'android')) {
      onPlayAsGuest();
    } else {
      if (typeof onGetStarted === 'function') onGetStarted();
    }
  };

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;
  const scaleAnim = useRef(new Animated.Value(0.8)).current;
  const starAnimations = useRef(
    Array.from({ length: 20 }, () => new Animated.Value(0))
  ).current;

  useEffect(() => {
    // Main content animation
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        tension: 50,
        friction: 7,
        useNativeDriver: true
      })
    ]).start();
  }, []);

  return (
    <View style={{ flex: 1, backgroundColor: '#030510' }}>
      <ImageBackground
        source={require('../../../assets/iqes/sunset-cross.png')}
        resizeMode="cover"
        style={{ flex: 1 }}
        imageStyle={{ opacity: 0.42 }}
      >
        <LinearGradient
          colors={['rgba(3,5,16,0.82)', 'rgba(9,5,26,0.92)', 'rgba(18,8,31,0.98)']}
          start={{ x: 0.08, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ flex: 1 }}
        >
          <LinearGradient
            colors={['rgba(255,213,74,0.16)', 'rgba(76,29,120,0.18)', 'rgba(3,5,16,0)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0.7 }}
            style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 360 }}
          />

        <SafeAreaView style={[styles.container, { paddingHorizontal: isPhoneLayout ? 24 : 36 }]}>
          <Stars starAnimations={starAnimations} />
          <Sparkles />

          <ScrollView
            bounces={Platform.OS === 'ios'}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{
              flexGrow: 1,
            }}
          >
            <View
              style={{
                width: '100%',
                maxWidth: 1180,
                alignSelf: 'center',
                paddingTop: isPhoneLayout ? spacing.lg : spacing['2xl'],
                paddingBottom: isPhoneLayout ? spacing.xl : spacing['3xl'],
                flexGrow: 1,
              }}
            >
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: isPhoneLayout ? spacing.lg : spacing['2xl'],
              }}
            >
              <View
                style={{
                  paddingHorizontal: 14,
                  paddingVertical: 8,
                  borderRadius: 999,
                  backgroundColor: 'rgba(10, 8, 24, 0.72)',
                  borderWidth: 1,
                  borderColor: 'rgba(246, 190, 58, 0.36)',
                }}
              >
                <Text
                  style={{
                    color: '#FFE8A3',
                    fontSize: 12,
                    fontWeight: '900',
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                  }}
                >
                  {t('landing.journeyBadge', language)}
                </Text>
              </View>
              <LandingLanguageSelector style={{ position: 'relative', top: 0, right: 0 }} />
            </View>

            <View
              style={{
                flex: 1,
                flexDirection: isWideLayout ? 'row' : 'column',
                justifyContent: isWideLayout ? 'center' : 'flex-start',
                alignItems: isWideLayout ? 'stretch' : 'center',
                gap: isPhoneLayout ? spacing.xl : spacing['3xl'],
              }}
            >
              <Animated.View
                style={{
                  flex: isWideLayout ? 0.95 : undefined,
                  width: '100%',
                  maxWidth: isWideLayout ? 500 : 760,
                  justifyContent: isWideLayout ? 'center' : 'flex-start',
                  opacity: fadeAnim,
                  transform: [{ translateY: slideAnim }],
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: '900',
                    letterSpacing: 1.4,
                    textTransform: 'uppercase',
                    color: '#F6BE3A',
                    textAlign: isWideLayout ? 'left' : 'center',
                    marginBottom: spacing.sm,
                  }}
                >
                  {t('landing.masteryEyebrow', language)}
                </Text>

                <Text
                  style={{
                    fontSize: isPhoneLayout ? 42 : 78,
                    fontWeight: '900',
                    color: '#FFF8E8',
                    textAlign: isWideLayout ? 'left' : 'center',
                    letterSpacing: 0,
                    lineHeight: isPhoneLayout ? 46 : 80,
                    marginBottom: spacing.md,
                    textShadowColor: 'rgba(246,190,58,0.22)',
                    textShadowOffset: { width: 0, height: 3 },
                    textShadowRadius: 14,
                  }}
                  accessibilityLabel="Kingdom Bible Quiz"
                  accessibilityRole="header"
                >
                  {isPhoneLayout ? 'KINGDOM BIBLE QUIZ' : 'KINGDOM\nBIBLE QUIZ'}
                </Text>

                <Text
                  style={{
                    fontSize: isPhoneLayout ? 18 : 25,
                    fontWeight: '800',
                    color: '#FFE8A3',
                    textAlign: isWideLayout ? 'left' : 'center',
                    lineHeight: isPhoneLayout ? 26 : 36,
                    marginBottom: spacing.sm,
                  }}
                >
                  {t('landing.heroLead', language)}
                </Text>

                <Text
                  style={{
                    fontSize: isPhoneLayout ? 15 : 18,
                    fontWeight: '500',
                    color: 'rgba(255,248,232,0.78)',
                    textAlign: isWideLayout ? 'left' : 'center',
                    lineHeight: isPhoneLayout ? 22 : 30,
                    maxWidth: 520,
                    alignSelf: isWideLayout ? 'flex-start' : 'center',
                    marginBottom: isPhoneLayout ? spacing.lg : spacing.xl,
                  }}
                >
                  {t('landing.heroBody', language)}
                </Text>

                <View
                  style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 10,
                    justifyContent: isWideLayout ? 'flex-start' : 'center',
                    marginBottom: isPhoneLayout ? spacing.lg : spacing.xl,
                  }}
                >
                  {featureChips.map((chip) => (
                    <View
                      key={chip}
                      style={{
                        borderRadius: 999,
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        backgroundColor: 'rgba(255,248,232,0.08)',
                        borderWidth: 1,
                        borderColor: 'rgba(246, 190, 58, 0.28)',
                      }}
                    >
                      <Text
                        style={{
                          color: '#FFF8E8',
                          fontSize: 12,
                          fontWeight: '800',
                        }}
                      >
                        {chip}
                      </Text>
                    </View>
                  ))}
                </View>

                <View
                  style={{
                    width: '100%',
                    maxWidth: isWideLayout ? 420 : 520,
                    alignSelf: isWideLayout ? 'flex-start' : 'center',
                    borderRadius: 28,
                    padding: isPhoneLayout ? 18 : 22,
                    backgroundColor: 'rgba(9, 5, 26, 0.9)',
                    borderWidth: 1,
                    borderColor: 'rgba(246, 190, 58, 0.42)',
                    shadowColor: '#F6BE3A',
                    shadowOffset: { width: 0, height: 18 },
                    shadowOpacity: 0.22,
                    shadowRadius: 28,
                    elevation: 10,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 12,
                      fontWeight: '700',
                      letterSpacing: 1.5,
                      textTransform: 'uppercase',
                      color: 'rgba(255,232,163,0.82)',
                      textAlign: isWideLayout ? 'left' : 'center',
                      marginBottom: spacing.sm,
                    }}
                  >
                    {t('landing.ctaEyebrow', language)}
                  </Text>

                  <AnimatedButton
                    buttonScaleAnim={buttonScaleAnim}
                    handleGetStarted={handleStartPlaying}
                    showConfetti={showConfetti}
                  />

                  {onPlayAsGuest && onGetStarted && (Platform.OS === 'ios' || Platform.OS === 'android') && (
                    <TouchableOpacity
                      onPress={onGetStarted}
                      style={{ marginTop: spacing.md, paddingVertical: 8 }}
                      activeOpacity={0.6}
                      accessibilityRole="button"
                      accessibilityLabel={t('auth.alreadyHaveAccount', language)}
                    >
                      <Text
                        style={{
                          color: 'rgba(255,248,232,0.76)',
                          fontSize: 13,
                          fontWeight: '500',
                          textAlign: isWideLayout ? 'left' : 'center',
                          letterSpacing: 0.2,
                        }}
                      >
                        {t('auth.alreadyHaveAccount', language)}
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
              </Animated.View>

              <Animated.View
                style={{
                  flex: isWideLayout ? 1.05 : undefined,
                  width: '100%',
                  maxWidth: isWideLayout ? 600 : 760,
                  opacity: fadeAnim,
                  transform: [{ scale: scaleAnim }],
                }}
              >
                <LinearGradient
                  colors={['rgba(13, 10, 30, 0.94)', 'rgba(8, 10, 24, 0.96)']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={{
                    borderRadius: 30,
                    padding: isPhoneLayout ? 18 : 30,
                    borderWidth: 1,
                    borderColor: 'rgba(246, 190, 58, 0.34)',
                    shadowColor: '#F6BE3A',
                    shadowOffset: { width: 0, height: 20 },
                    shadowOpacity: 0.16,
                    shadowRadius: 36,
                    elevation: 12,
                  }}
                >
                  <View>
                    <VerseCard
                      fadeAnim={fadeAnim}
                      slideAnim={slideAnim}
                      scaleAnim={scaleAnim}
                      isCardHovered={isCardHovered}
                      setCardHovered={setCardHovered}
                      reduceMotion={reduceMotion}
                      compact={isPhoneLayout}
                      isExpanded={isVerseExpanded}
                      onToggleExpand={() => setIsVerseExpanded((prev) => !prev)}
                    />
                  </View>

                  <View
                    style={{
                      paddingTop: isPhoneLayout ? spacing.lg : spacing.xl,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 13,
                        fontWeight: '900',
                        letterSpacing: 1.4,
                        textTransform: 'uppercase',
                        color: '#F6BE3A',
                        textAlign: isWideLayout ? 'left' : 'center',
                        marginBottom: spacing.sm,
                      }}
                    >
                      {t('landing.verseEyebrow', language)}
                    </Text>

                    <Text
                      style={{
                        color: 'rgba(255,248,232,0.78)',
                        fontSize: isPhoneLayout ? 14 : 16,
                        lineHeight: isPhoneLayout ? 21 : 26,
                        textAlign: isWideLayout ? 'left' : 'center',
                      }}
                    >
                      {t('landing.verseSupport', language)}
                    </Text>

                    <View
                      style={{
                        flexDirection: 'row',
                        marginTop: spacing.lg,
                        gap: 16,
                        flexWrap: 'wrap',
                        justifyContent: isWideLayout ? 'flex-start' : 'center',
                        paddingHorizontal: isWideLayout ? 0 : 12,
                      }}
                    >
                      <TouchableOpacity
                        onPress={() => Linking.openURL(`https://kingdombiblequiz.com/privacy?lang=${language}`)}
                        accessibilityRole="link"
                        accessibilityLabel={t('landing.privacyPolicy', language)}
                      >
                        <Text
                          style={{
                            color: 'rgba(255,248,232,0.68)',
                            fontSize: 13,
                            textDecorationLine: 'underline',
                            fontWeight: '500',
                          }}
                        >
                          {t('landing.privacyPolicy', language)}
                        </Text>
                      </TouchableOpacity>

                      <Text style={{ color: 'rgba(246,190,58,0.64)', fontSize: 13 }}>•</Text>

                      <TouchableOpacity
                        onPress={() => Linking.openURL(`https://kingdombiblequiz.com/terms?lang=${language}`)}
                        accessibilityRole="link"
                        accessibilityLabel={t('landing.termsOfService', language)}
                      >
                        <Text
                          style={{
                            color: 'rgba(255,248,232,0.68)',
                            fontSize: 13,
                            textDecorationLine: 'underline',
                            fontWeight: '500',
                          }}
                        >
                          {t('landing.termsOfService', language)}
                        </Text>
                      </TouchableOpacity>
                    </View>

                    {Platform.OS === 'web' && (
                      <View
                        style={{
                          flexDirection: 'row',
                          marginTop: spacing.lg,
                          gap: 12,
                          justifyContent: 'center',
                          alignItems: 'center',
                          paddingHorizontal: 12,
                        }}
                      >
                        {(typeof navigator === 'undefined' || !(/iPhone|iPad|iPod/i.test(navigator.userAgent))) && (
                          <TouchableOpacity
                            onPress={() => Linking.openURL('https://play.google.com/store/apps/details?id=kingdom.bible.quiz')}
                            accessibilityRole="link"
                            accessibilityLabel={t('landing.getGooglePlay', language)}
                            style={{
                              height: 40,
                              width: 135,
                              justifyContent: 'center',
                              alignItems: 'center',
                              overflow: 'hidden',
                              opacity: 0.94,
                            }}
                          >
                            <Image
                              source={{ uri: getGooglePlayBadgeUrl(language) }}
                              style={{ height: 58, width: 135, resizeMode: 'contain' }}
                              accessibilityLabel={t('landing.getGooglePlay', language)}
                            />
                          </TouchableOpacity>
                        )}

                        {(typeof navigator === 'undefined' || !(/Android/i.test(navigator.userAgent))) && (
                          <TouchableOpacity
                            onPress={() => Linking.openURL('https://apps.apple.com/us/app/kingdom-bible-quiz-app/id6753972354')}
                            accessibilityRole="link"
                            accessibilityLabel={t('landing.downloadAppStore', language)}
                            style={{
                              height: 40,
                              width: 120,
                              justifyContent: 'center',
                              alignItems: 'center',
                              overflow: 'hidden',
                              opacity: 0.94,
                            }}
                          >
                            <Image
                              source={{ uri: getAppStoreBadgeUrl(language) }}
                              style={{ height: 40, width: 120, resizeMode: 'contain' }}
                              accessibilityLabel={t('landing.downloadAppStore', language)}
                            />
                          </TouchableOpacity>
                        )}
                      </View>
                    )}
                  </View>
                </LinearGradient>
              </Animated.View>
            </View>
            </View>
          </ScrollView>
        </SafeAreaView>
        </LinearGradient>
      </ImageBackground>
    </View>
  );
}

// Helper function to get localized Google Play badge URL
function getGooglePlayBadgeUrl(languageCode: string): string {
  // Google Play badge localization mapping
  const badgeMap: Record<string, string> = {
    en: 'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png',
    es: 'https://play.google.com/intl/es_es/badges/static/images/badges/es_badge_web_generic.png',
    fr: 'https://play.google.com/intl/fr_fr/badges/static/images/badges/fr_badge_web_generic.png',
    de: 'https://play.google.com/intl/de_de/badges/static/images/badges/de_badge_web_generic.png',
    pt: 'https://play.google.com/intl/pt_br/badges/static/images/badges/pt-br_badge_web_generic.png',
    ru: 'https://play.google.com/intl/ru_ru/badges/static/images/badges/ru_badge_web_generic.png',
    it: 'https://play.google.com/intl/it_it/badges/static/images/badges/it_badge_web_generic.png',
    ko: 'https://play.google.com/intl/ko_kr/badges/static/images/badges/ko_badge_web_generic.png',
  };

  return badgeMap[languageCode] || badgeMap.en;
}

// Helper function to get localized Apple App Store badge URL
function getAppStoreBadgeUrl(languageCode: string): string {
  // Apple App Store badge localization mapping
  const badgeMap: Record<string, string> = {
    en: 'https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg',
    es: 'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/es-es',
    fr: 'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/fr-fr',
    de: 'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/de-de',
    pt: 'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/pt-br',
    ru: 'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/ru-ru',
    it: 'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/it-it',
    ko: 'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/ko-kr',
  };

  return badgeMap[languageCode] || badgeMap.en;
}
